/**
 * Permission rules (spec §6). The OS lets iOS ask ONCE, so the system prompt
 * is only ever shown after our own pre-prompt's "Allow"; a denial is
 * recovered through Settings.
 */
export type ShellPermission = 'notifications' | 'camera' | 'biometrics';
export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unavailable';
export type PermissionAction = 'none' | 'pre-prompt' | 'settings' | 'unavailable';

export const PRE_PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export function permissionAction(
  state: PermissionState | undefined,
  ctx: { userInitiated: boolean; prePromptDeclinedAt: number | null; now: number },
): PermissionAction {
  if (state === 'granted') return 'none';
  if (state === 'unavailable') return 'unavailable';
  if (state === 'denied') return 'settings';
  const coolingDown = ctx.prePromptDeclinedAt !== null && ctx.now - ctx.prePromptDeclinedAt < PRE_PROMPT_COOLDOWN_MS;
  if (coolingDown && !ctx.userInitiated) return 'none';
  return 'pre-prompt';
}

/** Offer Face ID / biometric sign-in after the second password sign-in (spec §6). */
export function shouldOfferBiometricUnlock(s: {
  passwordSignIns: number; biometrics: PermissionState | undefined; enabled: boolean; declined: boolean;
}): boolean {
  if (s.enabled || s.declined || s.passwordSignIns < 2) return false;
  return s.biometrics === 'prompt' || s.biometrics === 'granted';
}

export const PERMISSION_COPY: Record<ShellPermission, { title: (p: 'ios' | 'android') => string; body: string; offLine: string }> = {
  notifications: {
    title: () => 'Turn on notifications',
    body: "Get your schedule and shift changes the moment they're published.",
    offLine: 'Notifications are off for RubyTill.',
  },
  camera: {
    title: () => 'Allow camera',
    body: 'Scan barcodes, take product photos and clock in.',
    offLine: 'Camera is off for RubyTill.',
  },
  biometrics: {
    title: (p) => (p === 'ios' ? 'Use Face ID' : 'Use fingerprint or face unlock'),
    body: 'Sign in quickly and securely.',
    offLine: 'Biometric sign-in is off for RubyTill.',
  },
};
